import * as React from "react";
import { Helmet } from "react-helmet-async";
import { Avatar, Box, Chip, Paper, Stack, Tab, Tabs, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import secureLocalStorage from "react-secure-storage";
import UserPage from "./UserInfo";
import ChangePassword from "./ChangePassword";
import TwoFactorSettings from "../twoFactor/TwoFactorSettings";
import { colors } from "../brand";

export default function UserAccountPage() {
  const [tab, setTab] = React.useState("profile");
  const user = React.useMemo(() => {
    try {
      return JSON.parse(secureLocalStorage.getItem("profile"))?.results || {};
    } catch {
      return {};
    }
  }, []);
  const initials = (
    (user.firstName?.[0] || "U") + (user.lastName?.[0] || "")
  ).toUpperCase();

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Helmet title="My Account | Reconciliation" />

      <Paper
        elevation={0}
        sx={{
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          overflow: "hidden",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ p: { xs: 2, md: 2.5 } }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              fontSize: 20,
              fontWeight: 800,
              bgcolor: colors.primary,
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontSize: 20, fontWeight: 800, color: colors.textPrimary }}
            >
              {user.firstName} {user.lastName}
            </Typography>
            <Typography noWrap sx={{ fontSize: 13.5, color: colors.textSecondary }}>
              {user.email}
            </Typography>
            <Stack direction="row" spacing={0.8} sx={{ mt: 0.8 }} flexWrap="wrap" useFlexGap>
              {user.reconciliation && (
                <Chip
                  size="small"
                  label={`Reconciliation: ${user.reconciliation}`}
                  sx={{ fontWeight: 700, bgcolor: colors.secondarySoft, color: colors.secondaryDark }}
                />
              )}
              {user.branch && (
                <Chip
                  size="small"
                  label={user.branch}
                  sx={{ fontWeight: 600, textTransform: "capitalize" }}
                />
              )}
            </Stack>
          </Box>
        </Stack>

        <Tabs
          value={tab}
          onChange={(e, v) => setTab(v)}
          sx={{
            px: 1.5,
            borderTop: `1px solid ${colors.border}`,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 700,
              minHeight: 48,
            },
            "& .Mui-selected": { color: `${colors.primary} !important` },
            "& .MuiTabs-indicator": { backgroundColor: colors.secondary, height: 3 },
          }}
        >
          <Tab
            value="profile"
            icon={<PersonOutlineIcon fontSize="small" />}
            iconPosition="start"
            label="Profile"
          />
          <Tab
            value="security"
            icon={<LockOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label="Password & sign-in"
          />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {tab === "profile" ? (
          <UserPage />
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 2,
              alignItems: "start",
              // Line the authenticator card up with the password card
              "& .tf-panel--card": {
                margin: 0,
                maxWidth: "none",
                boxShadow: "none",
                borderColor: colors.border,
                padding: "20px",
              },
            }}
          >
            <ChangePassword />
            <TwoFactorSettings />
          </Box>
        )}
      </Box>
    </Box>
  );
}
